import React, { useState, useEffect } from 'react';
import { Patient } from '../../../services/firebase/FirebasePatientService';
import { firebaseAppointmentService, Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import {
  FirebaseTreatmentPlanService,
  TreatmentPlan
} from '../../../services/firebase/FirebaseTreatmentPlanService';

const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

interface NotesSidebarProps {
  client: Patient;
  providerId: string;
  noteDate: string; // YYYY-MM-DD
}

// CPT code mapping (mirrors BillingTab)
const SESSION_CPT: Record<string, { code: string; label: string; fee: number }> = {
  'initial-consultation': { code: '90791', label: 'Diagnostic Evaluation', fee: 250 },
  'therapy':             { code: '90837', label: 'Psychotherapy 53+ min', fee: 175 },
  'follow-up':           { code: '90834', label: 'Psychotherapy 38-52 min', fee: 150 },
  'assessment':          { code: '96130', label: 'Psych Testing Eval', fee: 200 },
  'couples':             { code: '90847', label: 'Family/Couples Therapy', fee: 200 },
  'other':               { code: '90837', label: 'Psychotherapy 53+ min', fee: 175 },
};

const NotesSidebar: React.FC<NotesSidebarProps> = ({ client, providerId, noteDate }) => {
  const [activeTab, setActiveTab] = useState<'appointment' | 'treatment'>('appointment');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [loading, setLoading] = useState(true);

  // Load appointments for this client
  useEffect(() => {
    if (!client.id || !providerId) return;
    const load = async () => {
      try {
        const appts = await firebaseAppointmentService.getAppointments({
          patientId: client.id,
          providerId,
        });
        // Sort by date descending
        appts.sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime());
        setAppointments(appts);
      } catch (err) {
        console.error('NotesSidebar: Error loading appointments', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [client.id, providerId]);

  // Subscribe to treatment plans (all providers — director needs cross-provider view)
  useEffect(() => {
    if (!client.id) return;
    const unsub = FirebaseTreatmentPlanService.subscribeToPatientPlans(
      client.id, (data) => setPlans(data)
    );
    return unsub;
  }, [client.id]);

  // Find the appointment matching the note date (or closest)
  const matchingAppt = appointments.find(a => {
    const d = a.appointmentDate instanceof Date
      ? a.appointmentDate.toISOString().split('T')[0]
      : String(a.appointmentDate).split('T')[0];
    return d === noteDate;
  });

  // Find prev/next appointments relative to the matching one
  const sortedAsc = [...appointments].sort(
    (a, b) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime()
  );
  const matchIdx = matchingAppt ? sortedAsc.findIndex(a => a.id === matchingAppt.id) : -1;
  const prevAppt = matchIdx > 0 ? sortedAsc[matchIdx - 1] : null;
  const nextAppt = matchIdx >= 0 && matchIdx < sortedAsc.length - 1 ? sortedAsc[matchIdx + 1] : null;

  const statusColor = (s: string) => {
    switch (s) {
      case 'active': case 'in-progress': return '#D4A017';
      case 'achieved': case 'completed': return '#10B981';
      case 'discontinued': case 'modified': return '#EF4444';
      case 'not-started': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const formatApptDate = (d: Date | string) => {
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '8px 4px',
    background: isActive ? '#28527A' : 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid #58A6FF' : '2px solid transparent',
    color: isActive ? '#58A6FF' : '#9ca3af',
    fontSize: '12px',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
    textAlign: 'center' as const,
  });

  return (
    <div style={{
      width: '280px',
      flexShrink: 0,
      background: '#1A3A5C',
      borderRadius: '8px',
      border: '1px solid #2A5470',
      overflow: 'hidden',
    }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #2A5470' }}>
        <button style={tabStyle(activeTab === 'appointment')} onClick={() => setActiveTab('appointment')}>
          Appointment info
        </button>
        <button style={tabStyle(activeTab === 'treatment')} onClick={() => setActiveTab('treatment')}>
          Treatment progress
        </button>
      </div>

      <div style={{ padding: '16px', fontSize: '13px' }}>
        {activeTab === 'appointment' ? (
          <AppointmentInfoTab
            matchingAppt={matchingAppt}
            prevAppt={prevAppt}
            nextAppt={nextAppt}
            loading={loading}
            noteDate={noteDate}
            formatApptDate={formatApptDate}
          />
        ) : (
          <TreatmentProgressTab plans={plans} statusColor={statusColor} />
        )}
      </div>
    </div>
  );
};

// ─── Appointment Info Tab ───

const AppointmentInfoTab: React.FC<{
  matchingAppt: Appointment | undefined;
  prevAppt: Appointment | null;
  nextAppt: Appointment | null;
  loading: boolean;
  noteDate: string;
  formatApptDate: (d: Date | string) => string;
}> = ({ matchingAppt, prevAppt, nextAppt, loading, noteDate, formatApptDate }) => {
  if (loading) {
    return <p style={{ color: '#d1d5db', fontStyle: 'italic' }}>Loading...</p>;
  }

  if (!matchingAppt) {
    return (
      <div>
        <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: '0 0 12px' }}>
          No appointment found for {noteDate}.
        </p>
        {(prevAppt || nextAppt) && (
          <div style={{ borderTop: '1px solid #2A5470', paddingTop: '12px' }}>
            <p style={{ color: '#d1d5db', fontSize: '11px', margin: '0 0 8px', textTransform: 'uppercase', fontWeight: 600 }}>
              Appointments
            </p>
            {prevAppt && <ApptLink appt={prevAppt} label="Previous" formatDate={formatApptDate} />}
            {nextAppt && <ApptLink appt={nextAppt} label="Next" formatDate={formatApptDate} />}
          </div>
        )}
      </div>
    );
  }

  const cpt = SESSION_CPT[matchingAppt.sessionType] || SESSION_CPT['other'];
  const apptDate = matchingAppt.appointmentDate instanceof Date
    ? matchingAppt.appointmentDate
    : new Date(matchingAppt.appointmentDate);

  return (
    <div>
      {/* Details section */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <span style={{ color: '#58A6FF', fontSize: '14px' }}>&#128197;</span>
          <span style={{ color: '#e5e7eb', fontWeight: 500 }}>
            {formatApptDate(apptDate)}
          </span>
        </div>
        <div style={{ color: '#d1d5db', marginBottom: '4px' }}>
          {formatTime12Hour(matchingAppt.startTime)} - {formatTime12Hour(matchingAppt.endTime)} ({matchingAppt.duration} min)
        </div>
        {matchingAppt.providerName && (
          <div style={{ color: '#d1d5db', marginBottom: '4px' }}>
            {matchingAppt.providerName}
          </div>
        )}
      </div>

      {/* Services */}
      <div style={{
        borderTop: '1px solid #2A5470',
        paddingTop: '12px',
        marginBottom: '16px'
      }}>
        <p style={{ color: '#d1d5db', fontSize: '11px', margin: '0 0 8px', textTransform: 'uppercase', fontWeight: 600 }}>
          Services
        </p>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '4px'
        }}>
          <div>
            <span style={{ color: '#e5e7eb' }}>{cpt.code}</span>
            <span style={{ color: '#d1d5db' }}> {cpt.label}</span>
          </div>
          <span style={{ color: '#e5e7eb', fontWeight: 500 }}>${cpt.fee}</span>
        </div>
      </div>

      {/* Billing */}
      <div style={{
        borderTop: '1px solid #2A5470',
        paddingTop: '12px',
        marginBottom: '16px'
      }}>
        <p style={{ color: '#d1d5db', fontSize: '11px', margin: '0 0 8px', textTransform: 'uppercase', fontWeight: 600 }}>
          Billing
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ color: '#d1d5db' }}>Status</span>
          <span style={{
            color: matchingAppt.status === 'completed' ? '#10b981' : '#f59e0b',
            fontWeight: 500,
            textTransform: 'capitalize'
          }}>
            {matchingAppt.status}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#d1d5db' }}>Appointment total</span>
          <span style={{ color: '#e5e7eb', fontWeight: 600 }}>${cpt.fee}</span>
        </div>
      </div>

      {/* Previous / Next Appointments */}
      {(prevAppt || nextAppt) && (
        <div style={{
          borderTop: '1px solid #2A5470',
          paddingTop: '12px'
        }}>
          <p style={{ color: '#d1d5db', fontSize: '11px', margin: '0 0 8px', textTransform: 'uppercase', fontWeight: 600 }}>
            Appointments
          </p>
          {prevAppt && <ApptLink appt={prevAppt} label="Previous" formatDate={formatApptDate} />}
          {nextAppt && <ApptLink appt={nextAppt} label="Next" formatDate={formatApptDate} />}
        </div>
      )}
    </div>
  );
};

// ─── Appointment Link ───

const ApptLink: React.FC<{
  appt: Appointment;
  label: string;
  formatDate: (d: Date | string) => string;
}> = ({ appt, label, formatDate }) => {
  const d = appt.appointmentDate instanceof Date ? appt.appointmentDate : new Date(appt.appointmentDate);
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 0',
    }}>
      <div>
        <span style={{ color: '#d1d5db', fontSize: '11px' }}>{label}</span>
        <div style={{ color: '#d1d5db', fontSize: '12px' }}>
          {formatDate(d)} &middot; {appt.startTime}
        </div>
      </div>
    </div>
  );
};

// ─── Treatment Progress Tab ───

const TreatmentProgressTab: React.FC<{
  plans: TreatmentPlan[];
  statusColor: (s: string) => string;
}> = ({ plans, statusColor }) => {
  const activePlans = plans.filter(p => p.status === 'active' || p.status === 'draft');

  if (activePlans.length === 0) {
    return (
      <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: 0 }}>
        No treatment plan created yet.
      </p>
    );
  }

  return (
    <div>
      {activePlans.map(plan => (
        <div key={plan.id} style={{ marginBottom: '16px' }}>
          {/* Plan header */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: '13px', marginBottom: '2px' }}>
              {plan.diagnosis || 'Untitled Plan'}
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{
                padding: '1px 6px',
                borderRadius: '8px',
                fontSize: '10px',
                fontWeight: 600,
                background: plan.status === 'active' ? '#D4A017' : '#6b7280',
                color: plan.status === 'active' ? '#1f2937' : '#fff',
                textTransform: 'capitalize'
              }}>
                {plan.status}
              </span>
              {plan.reviewDate && (
                <span style={{ color: '#d1d5db', fontSize: '11px' }}>
                  Review: {plan.reviewDate}
                </span>
              )}
            </div>
          </div>

          {/* Goals */}
          {plan.goals.map((goal, gi) => (
            <div key={goal.id} style={{
              marginBottom: '8px',
              paddingLeft: '8px',
              borderLeft: `2px solid ${statusColor(goal.status)}`
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: statusColor(goal.status),
                  flexShrink: 0,
                  marginTop: '4px'
                }} />
                <span style={{ color: '#e5e7eb', fontSize: '12px', fontWeight: 500 }}>
                  Goal {gi + 1}: {goal.description || '(No description)'}
                </span>
              </div>

              {/* Objectives */}
              {goal.objectives.map((obj, oi) => (
                <div key={obj.id} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  marginLeft: '14px',
                  marginBottom: '2px'
                }}>
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: statusColor(obj.status),
                    flexShrink: 0,
                    marginTop: '5px'
                  }} />
                  <span style={{ color: '#d1d5db', fontSize: '11px' }}>
                    {obj.description || `Objective ${oi + 1}`}
                    {obj.status !== 'not-started' && (
                      <span style={{ color: statusColor(obj.status), marginLeft: '4px', textTransform: 'capitalize' }}>
                        ({obj.status})
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default NotesSidebar;
